/**
 * Parses an API timestamp that was serialized without a timezone marker,
 * treating it as UTC.
 *
 * Backend datetimes are stored in UTC, but they round-trip out of MySQL with
 * `DateTime.Kind = Unspecified`, so the JSON reaching the browser is a naive
 * string with no trailing `Z`. `new Date()` interprets such strings as local
 * time, displaying the raw UTC value 1+ hours off. Appending the missing `Z`
 * lets the Date engine apply the correct offset for that specific instant,
 * which stays correct across DST changes (unlike subtracting today's
 * `getTimezoneOffset()`).
 *
 * Values that already carry a timezone marker, date-only strings, epoch
 * numbers, and Date instances pass through unchanged.
 */
export function parseUtcDate(value: string | number | Date): Date {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  const hasTimezoneMarker = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const hasTimeComponent = value.includes('T');
  return hasTimeComponent && !hasTimezoneMarker ? new Date(value + 'Z') : new Date(value);
}
